import { useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { CoaTemplateForm, useCreateCoaTemplate } from '~/features/coa-templates'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts/new` — create a Chart-of-Accounts
 * Templates row. A full page rather than a modal — the country picker +
 * fiscal-year fields don't fit comfortably in an overlay.
 */
export const Route = createFileRoute('/admin/finance/reference-accounts/new')({
  component: NewTemplatePage,
})

function NewTemplatePage(): ReactElement {
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const createMutation = useCreateCoaTemplate()

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button
            type="button"
            className="ka-btn ka-btn--ghost ka-btn--sm"
            onClick={() => void navigate({ to: '/admin/finance/reference-accounts' })}
          >
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">New Template</h1>
        </div>
      </header>

      <CoaTemplateForm
        template={null}
        isSubmitting={createMutation.isPending}
        serverError={serverError}
        onCancel={() => void navigate({ to: '/admin/finance/reference-accounts' })}
        onSubmit={(body) => {
          setServerError(null)
          createMutation.mutate(body as Parameters<typeof createMutation.mutate>[0], {
            onSuccess: (created) =>
              void navigate({
                to: '/admin/finance/reference-accounts/$templateId',
                params: { templateId: created.id },
              }),
            onError: (cause) => setServerError(errorToMessage(cause)),
          })
        }}
      />
    </div>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
